import { StatusBadge } from '@/components/common';
import { type MaturityModel, type MaturityModelGroup } from '@/domain/maturityModel/types';
import { useMaturityFilterStore } from '@/features/maturityModel/store/maturityFilter';
import { type GroupBy } from '@/features/maturityModel/types';
import { getHeader, getStatusColor } from '@/features/maturityModel/utils';
import { Column } from 'primereact/column';
import { DataTable, type DataTableExpandedRows, type DataTableValueArray } from 'primereact/datatable';
import { useMemo, useState } from 'react';

import MaturityGroupItemTable from './MaturityGroupItemTable';

const rowExpansionTemplate = (row: MaturityModelGroup) => <MaturityGroupItemTable row={row} />;

const getMaturityLevel = (row: MaturityModelGroup, groupBy: GroupBy) => {
  const items = row.items.filter((i) => i.selectedOption?.maturityLevel !== -1);

  return groupBy === 'Grouping by Average'
    ? items.reduce((a, i) => {
        if (!i.selectedOption) return a + 0;
        const maxPossibleMaturity = Math.max(...i.options.map((opt) => opt.maturityLevel));
        if (maxPossibleMaturity < 4 && i.selectedOption.maturityLevel === maxPossibleMaturity) {
          return a + Math.max(...row.items.map((item) => item.selectedOption?.maturityLevel ?? 0));
        }
        return a + i.selectedOption.maturityLevel;
      }, 0) / items.length
    : Math.min(...items.map((i) => i.selectedOption?.maturityLevel ?? 0));
};

interface MaturitySectionTableProps {
  maturity: MaturityModel;
}

const MaturitySectionTable = ({ maturity }: MaturitySectionTableProps) => {
  const groupBy = useMaturityFilterStore((s) => s.groupBy);
  const [expandedRows, setExpandedRows] = useState<DataTableValueArray | DataTableExpandedRows | undefined>([]);

  const groupedMaturity = useMemo(
    () => [...(maturity?.groups ?? [])].sort((a, b) => a.order - b.order),
    [maturity?.groups],
  );

  const renderHeaderRow = (row: MaturityModelGroup) => {
    const maturityLevel = Math.round(getMaturityLevel(row, groupBy));
    return (
      <span>
        {row.name} <StatusBadge color={getStatusColor(maturityLevel)}>{getHeader(maturityLevel)}</StatusBadge>
      </span>
    );
  };
  return (
    <DataTable
      value={groupedMaturity}
      size="small"
      expandedRows={expandedRows}
      onRowToggle={(e) => {
        setExpandedRows(e.data);
      }}
      rowExpansionTemplate={rowExpansionTemplate}
      showHeaders={false}
    >
      <Column expander style={{ width: '3em' }} />
      <Column field="name" body={(rowData: MaturityModelGroup) => renderHeaderRow(rowData)} />
    </DataTable>
  );
};

export default MaturitySectionTable;
